import { Injectable } from '@angular/core';
import { Entity, EntityType } from 'breeze-client';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { QuoteRequestViewComponent } from '.';
import { RfqResponseSideFormComponent } from '../../core/components/rfq-response-side-form/rfq-response-side-form.component';
import { BxLogger } from '../../core/services/bx-logger.service';
import { DialogService } from '../../core/services/dialog.service';
import { AsideModalSize, HelperService } from '../../core/services/helper.service';
import { StoresService } from '../../core/services/stores';
import { Round } from '../../data/logic/math.functions';
import { TaxService, TaxTypeEnum } from '../../data/logic/tax.service';
import {
    Estimate,
    EstimateItem,
    Job,
    JobItem,
    JobVariation,
    PurchaseOrder,
    QuoteRequest,
    QuoteRequestRecipient,
    QuoteRequestRecipientItem,
} from '../../data/models';
import { RepoEstimatesService } from '../../data/repositories/repo-estimates.service';
import { RepoJobsService } from '../../data/repositories/repo-jobs.service';
import { RepoQuoteRequestsService } from '../../data/repositories/repo-quoterequests.service';
import { BreezeService } from '../../data/services/breeze/breeze.service';
import { BreezeCase } from '../../utils/types/convert-case';
import { QuoteRequestListModel, QuoteRequestRecipientListModel } from './quoterequest-list/domain/entities/quote-request-list.model';
import { SenderQuoteStatus } from './quoterequest-list/domain/sender-quote-req-status.enum';
import { Uuid } from '../../../graphql';

@Injectable({ providedIn: 'root' })
export class QuoteRequestsService {
    quoteRequestRecipient = null;
    quoteRequest = null;

    // @ts-ignore TS2345
    private expandRecipientIdSource = new BehaviorSubject<string>(null);

    // TODO: Check if this can be removed once RFQ saving is debreezed
    private rfqCreated = new BehaviorSubject<boolean>(false);
    rfqCreated$ = this.rfqCreated.asObservable();

    constructor(
        private breezeService: BreezeService,
        private helperService: HelperService,
        private storesService: StoresService,
        private quoteRequestsRepo: RepoQuoteRequestsService,
        private estimatesRepo: RepoEstimatesService,
        private jobsRepo: RepoJobsService,
        private taxService: TaxService,
        private logger: BxLogger,
        private dialogService: DialogService
    ) {
        this.storesService.refreshQuoteRequestEvent$.subscribe((quoteRequestId) => {
            this.quoteRequestsRepo.Get(quoteRequestId);
        });
    }

    public get expandRecipientId$(): Observable<string> {
        return this.expandRecipientIdSource.asObservable();
    }

    public set expandRecipientId(id: string) {
        this.expandRecipientIdSource.next(id);
    }

    // @ts-ignore TS7006
    newQuoteRequest(options, estimate, job) {
        // Call Create method on repository.quoterequests
        // (will inject IDs and defaults on top of existing options)
        const quoteRequest = this.quoteRequestsRepo.Create(options);

        // Create items here
        let order = 0;
        const groups = {};

        // @ts-ignore TS7006
        options.items.forEach((sourceItem) => {
            // If the items group has not already been added, then do it now
            // @ts-ignore TS7053
            let groupId = groups[sourceItem.ParentID];
            if (!groupId) {
                let groupItem;
                if (estimate) {
                    // @ts-ignore TS7006
                    groupItem = estimate.EstimateItems.find((e) => e.ID_EstimateItem === sourceItem.ParentID);
                } else {
                    // @ts-ignore TS7006
                    groupItem = job.JobItems.find((e) => e.ID_JobItem === sourceItem.ParentID);
                }

                if (groupItem) {
                    const data = {
                        ID_QuoteRequest: quoteRequest.ID_QuoteRequest,
                        ID_EstimateItem: estimate ? groupItem.ID_EstimateItem : undefined,
                        ID_JobItem: job ? groupItem.ID_JobItem : undefined,
                        Description: groupItem.Description,
                        Order: order++,
                    };

                    const quoteRequestGroup = this.quoteRequestsRepo.CreateGroup(data);
                    groupId = quoteRequestGroup.ID_QuoteRequestGroup;
                    // @ts-ignore TS7053
                    groups[estimate ? groupItem.ID_EstimateItem : groupItem.ID_JobItem] = groupId;
                }
            }

            const data = $.extend(sourceItem, {
                ID_QuoteRequest: quoteRequest.ID_QuoteRequest,
                ID_QuoteRequestGroup: groupId,
                CalculationMethod: quoteRequest.CalculationMethod,
                Order: order++,
            });

            const quoteRequestItem = this.quoteRequestsRepo.CreateItem(data);

            if (sourceItem.Children && sourceItem.Children.length > 0) {
                quoteRequestItem.IsRecipe = true;
                // @ts-ignore TS7006
                sourceItem.Children.forEach((recipeItem) => {
                    const data = $.extend(recipeItem, {
                        ID_QuoteRequest: quoteRequest.ID_QuoteRequest,
                        ID_QuoteRequestItem: quoteRequestItem.ID_QuoteRequestItem,
                        CalculationMethod: quoteRequest.CalculationMethod,
                        Order: order++,
                    });

                    this.quoteRequestsRepo.CreateRecipeItem(data);
                });
            }
        });

        return this.viewBreezeQuoteRequest(quoteRequest, estimate, job);
    }

    async viewQuoteRequest(quoteRequest: QuoteRequestListModel, estimate?: Estimate, job?: Job) {
        const breezeQuoteRequestEntity = await this.quoteRequestsRepo.Get(quoteRequest.iD_QuoteRequest);
        let breezeJob: Job | undefined = job;
        let breezeEstimate: Estimate | undefined = estimate;
        if (quoteRequest.iD_Job && !breezeJob) {
            const breezeJobEntity = await this.jobsRepo.Get(quoteRequest.iD_Job);
            breezeJob = breezeJobEntity as unknown as Job;
        }
        if (quoteRequest.iD_Estimate && !breezeEstimate) {
            const breezeEstimateEntity = await this.estimatesRepo.Get(quoteRequest.iD_Estimate);
            breezeEstimate = breezeEstimateEntity as unknown as Estimate;
        }
        const breezeQuoteRequest = breezeQuoteRequestEntity as unknown as BreezeCase<QuoteRequest>;
        return await this.viewBreezeQuoteRequest(breezeQuoteRequest, breezeEstimate, breezeJob);
    }

    async viewBreezeQuoteRequest(quoteRequest: BreezeCase<QuoteRequest>, estimate?: Estimate, job?: Job) {
        const doSave = async (qr: QuoteRequest) => {
            await this._setQuoteRequestNumber(qr);
            await this._saveQuoteRequest(qr);
        };
        const initialState = {
            quoteRequest,
            doSave,
            estimate,
            job,
        };
        const modalRef = this.helperService.showModalAside(QuoteRequestViewComponent, { initialState }, AsideModalSize.normal);
        const result = await firstValueFrom(modalRef.content!.action);
        if (!result.cancelled) {
            return result.quoteRequest as QuoteRequest;
        } else {
            this._cancelQuoteRequest(result.quoteRequest);
            throw Error('Cancelled');
        }
    }

    viewQuoteRequestRecipientPanel(quoteRequestId: string, quoteRequestRecipientId: string) {
        const promise = new Promise((resolve, reject) => {
            const modalRef = this.helperService.showModalAside(
                RfqResponseSideFormComponent,
                { initialState: { quoteRequestId, quoteRequestRecipientId } },
                AsideModalSize.wide
            );
            // @ts-ignore TS2532
            modalRef.content.closemodal.subscribe((result) => {
                if (result.cancelled) {
                    reject();
                } else {
                    resolve(result.updatedRecipient);
                }
            });
        });
        return promise;
    }

    _cancelQuoteRequest(quoteRequest: BreezeCase<QuoteRequest> & Entity) {
        const quoteRequestGroupType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestGroup');
        const quoteRequestItemType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestItem');
        const quoteRequestRecipeItemType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipeItem');
        const quoteRequestRecipientType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipient');
        const quoteRequestRecipientGroupType = <EntityType>(
            this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipientGroup')
        );
        const quoteRequestRecipientItemType = <EntityType>(
            this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipientItem')
        );
        const quoteRequestRecipientRecipeItemType = <EntityType>(
            this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipientRecipeItem')
        );
        const changes = this.breezeService.manager.getChanges([
            quoteRequestGroupType,
            quoteRequestItemType,
            quoteRequestRecipeItemType,
            quoteRequestRecipientType,
            quoteRequestRecipientGroupType,
            quoteRequestRecipientItemType,
            quoteRequestRecipientRecipeItemType,
        ]);
        changes.forEach((item) => {
            item.entityAspect.rejectChanges();
        });
        quoteRequest.entityAspect.rejectChanges();
    }

    // @ts-ignore TS7006
    _setQuoteRequestNumber(quoteRequest) {
        if (quoteRequest.RequestNumber === 0) {
            return this.quoteRequestsRepo.GetNewQuoteRequestNumber().then((result) => {
                quoteRequest.RequestNumber = result.data;
                return quoteRequest;
            });
        } else return Promise.resolve(quoteRequest);
    }

    // @ts-ignore TS7006
    _saveQuoteRequest(quoteRequest) {
        return this._saveQuoteRequestsEntities().then(() => {
            return this.quoteRequestsRepo.Save().then(() => {
                // Do sending part here if we flagged it.
                if (quoteRequest.Send) {
                    // TODO: Check if this can be removed once RFQ saving is debreezed
                    this.rfqCreated.next(true);

                    return this.quoteRequestsRepo.Send(quoteRequest.ID_QuoteRequest).then(() => {
                        // to get correct count for status = open
                        this.storesService.refreshQuoteRequests();
                        return quoteRequest;
                    });
                }
                if (quoteRequest.Updated) {
                    this.quoteRequestsRepo.Update(quoteRequest.ID_QuoteRequest);
                    this.dialogService.show({
                        title: 'You updated an open Quote Request',
                        text: "We've notified the recipients that this quote request has been updated.",
                        type: 'info',
                        showCancel: false,
                    });
                }

                this.storesService.refreshQuoteRequests();

                return quoteRequest;
            });
        });
    }

    _saveQuoteRequestsEntities() {
        // Ensures that quote request entities are saved in the correct order to the backend
        // @ts-ignore TS7034
        const entities = [];
        const quoteRequestType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequest');
        const quoteRequestGroupType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestGroup');
        const quoteRequestItemType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestItem');
        const quoteRequestRecipeItemType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipeItem');
        const quoteRequestRecipientType = <EntityType>this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipient');
        const quoteRequestRecipientItemType = <EntityType>(
            this.breezeService.manager.metadataStore.getEntityType('QuoteRequestRecipientItem')
        );
        const changes = this.breezeService.manager.getChanges([
            quoteRequestType,
            quoteRequestGroupType,
            quoteRequestItemType,
            quoteRequestRecipeItemType,
            quoteRequestRecipientType,
            quoteRequestRecipientItemType,
        ]);
        // @ts-ignore TS7005
        Array.prototype.push.apply(entities, changes);
        // @ts-ignore TS7005
        return this.quoteRequestsRepo.Save(entities, 'Quote Request Saved');
    }

    recoverQuoteRequest(quoteRequest: QuoteRequestListModel) {
        const params = {
            title: 'Recover Quote Request?',
            text: '',
        };

        return this.dialogService.show(params).then(
            () => {
                // Get Estimate Items or Job Items for this quoterequest we're recovering
                const requestList = [this.quoteRequestsRepo.Get(quoteRequest.iD_QuoteRequest)];
                if (quoteRequest.iD_Job) requestList.push(this.jobsRepo.Get(quoteRequest.iD_Job));
                else if (quoteRequest.iD_Estimate) requestList.push(this.estimatesRepo.Get(quoteRequest.iD_Estimate));

                return Promise.all(requestList).then((data) => {
                    // @ts-ignore TS2571
                    const estimateItems = data[1]?.EstimateItems;
                    // @ts-ignore TS2571
                    const jobItems = data[1]?.JobItems;
                    const request = data[0];
                    // @ts-ignore TS2571
                    const requestItems = request.QuoteRequestItems;
                    // @ts-ignore TS2571
                    const requestRecipeItems = request.QuoteRequestRecipeItems;

                    // @ts-ignore TS7006
                    requestItems.forEach((item) => {
                        if (item.ID_JobItem) {
                            // @ts-ignore TS7006
                            const jobItem = jobItems.find((e) => e.ID_JobItem === item.ID_JobItem);
                            if (jobItem) {
                                // @ts-ignore TS2571
                                jobItem.ID_QuoteRequest = request.ID_QuoteRequest;
                                jobItem.ID_QuoteRequestItem = item.ID_QuoteRequestItem;
                            }
                        } else if (item.ID_EstimateItem) {
                            // Find the original estimate item, and restore it's quote request ID fields
                            // @ts-ignore TS7006
                            const estimateItem = estimateItems.find((e) => {
                                return e.ID_EstimateItem === item.ID_EstimateItem;
                            });
                            if (estimateItem) {
                                // @ts-ignore TS2571
                                estimateItem.ID_QuoteRequest = request.ID_QuoteRequest;
                                estimateItem.ID_QuoteRequestItem = item.ID_QuoteRequestItem;
                            }
                        }
                    });

                    // @ts-ignore TS7006
                    requestRecipeItems.forEach((recipeItem) => {
                        if (recipeItem.ID_EstimateItem) {
                            // Find the original estimate item, and restore it's quote request ID fields
                            // @ts-ignore TS7006
                            const estimateItem = estimateItems.find((e) => {
                                return e.ID_EstimateItem === recipeItem.ID_EstimateItem;
                            });
                            if (estimateItem) {
                                // @ts-ignore TS2571
                                estimateItem.ID_QuoteRequest = request.ID_QuoteRequest;
                                estimateItem.ID_QuoteRequestRecipeItem = recipeItem.ID_QuoteRequestRecipeItem;
                            }
                        }
                    });

                    // @ts-ignore TS2571
                    request.IsDeleted = false;
                    // @ts-ignore TS2571
                    request.DateDeleted = null;

                    return this.quoteRequestsRepo
                        .Save(null, 'Request for Quote recovered') // null saves all changed entities
                        .then(() => {
                            this.storesService.refreshQuoteRequests();
                        });
                });
            },
            () => {}
        );
    }

    async onSelectedRecipientChanged(quoteRequestRecipient: QuoteRequestRecipientListModel, quoteRequest: QuoteRequestListModel) {
        if (quoteRequest.iD_Estimate) {
            return await this.onSelectedRecipientChangedForEstimate(quoteRequestRecipient, quoteRequest);
        }
        if (quoteRequest.iD_Job) {
            return await this.onSelectedRecipientChangedForJob(quoteRequestRecipient, quoteRequest);
        }
    }

    private async onSelectedRecipientChangedForJob(
        quoteRequestRecipient: QuoteRequestRecipientListModel,
        quoteRequest: QuoteRequestListModel
    ) {
        if (!quoteRequest.iD_Job) throw Error('Only quote requests for jobs are supported.');
        const breezeQuoteRequestEntity = await this.quoteRequestsRepo.Get(quoteRequest.iD_QuoteRequest);
        const breezeQuoteRequest = breezeQuoteRequestEntity as unknown as BreezeCase<QuoteRequest>;
        breezeQuoteRequest.QuoteRequestRecipients?.forEach((recipient) => {
            if (recipient.ID_QuoteRequestRecipient === quoteRequestRecipient.iD_QuoteRequestRecipient) {
                recipient.IsSelectedQuote = quoteRequestRecipient.isSelectedQuote;
            } else {
                recipient.IsSelectedQuote = false;
            }
        });
        if (quoteRequestRecipient.isSelectedQuote) {
            breezeQuoteRequest.Status = SenderQuoteStatus.Accepted;
        } else {
            breezeQuoteRequest.Status = this.isQuoteRequestExpired(breezeQuoteRequest.DateDeadline)
                ? SenderQuoteStatus.Open
                : SenderQuoteStatus.Closed;
        }
        await this.quoteRequestsRepo.Save(null);
    }

    private async onSelectedRecipientChangedForEstimate(
        quoteRequestRecipient: QuoteRequestRecipientListModel,
        quoteRequest: QuoteRequestListModel
    ) {
        if (!quoteRequest.iD_Estimate) throw Error('Only quote requests for estimates are supported.');
        const [breezeQuoteRequestEntity, breezeRecipientItemsEntities, breezeEstimateEntity] = await Promise.all([
            this.quoteRequestsRepo.Get(quoteRequest.iD_QuoteRequest),
            this.quoteRequestsRepo.GetQuotedItems(quoteRequest.iD_QuoteRequest, quoteRequestRecipient.iD_QuoteRequestRecipient),
            this.estimatesRepo.Get(quoteRequest.iD_Estimate),
        ]);

        const breezeQuoteRequest = breezeQuoteRequestEntity as unknown as BreezeCase<QuoteRequest>;
        const breezeRecipientItems = breezeRecipientItemsEntities as unknown as BreezeCase<QuoteRequestRecipientItem>[];
        const breezeEstimate = breezeEstimateEntity as unknown as Estimate;
        const breezeRecipient = breezeQuoteRequest.QuoteRequestRecipients!.find(
            (qrr) => qrr.ID_QuoteRequestRecipient === quoteRequestRecipient.iD_QuoteRequestRecipient
        )!;

        // We want to apply items in their display order
        breezeRecipientItems.sort(this.SortExistingItemsFirst);

        // Create the child hierarchy for the estimate items (needed by Estimates.OrderEstimateItemsTree)
        const categoryItems = this.estimatesRepo.GetEstimateItemsTree(breezeEstimate.EstimateItems) as EstimateItem[];

        // Perform any undo action needed
        if (!quoteRequestRecipient.isSelectedQuote) {
            this.undoQuoteRequestRecipient(breezeEstimate, breezeRecipientItems, breezeRecipient);
        } else {
            const quoteRequestRecipientId = quoteRequestRecipient.iD_QuoteRequestRecipient;
            const allRecipients = breezeQuoteRequest.QuoteRequestRecipients ?? [];
            for (let i = 0; i < allRecipients.length; i++) {
                const breezeOtherRecipient = allRecipients[i]!;
                // Find other recipients that are selected, and unselect them
                if (breezeOtherRecipient.ID_QuoteRequestRecipient !== quoteRequestRecipientId && breezeOtherRecipient.IsSelectedQuote) {
                    const [breezeOtherRecipientItemsEntities] = await Promise.all([
                        this.quoteRequestsRepo.GetQuotedItems(quoteRequest.iD_QuoteRequest, breezeOtherRecipient.ID_QuoteRequestRecipient),
                        this.quoteRequestsRepo.GetQuotedGroups(quoteRequest.iD_QuoteRequest, breezeOtherRecipient.ID_QuoteRequestRecipient),
                    ]);
                    const breezeOtherRecipientItems =
                        breezeOtherRecipientItemsEntities as unknown as BreezeCase<QuoteRequestRecipientItem>[];
                    this.undoQuoteRequestRecipient(breezeEstimate, breezeOtherRecipientItems, breezeOtherRecipient);
                }
            }
        }

        if (quoteRequestRecipient.isSelectedQuote) {
            this.doQuoteRequestRecipient(breezeEstimate, categoryItems, breezeRecipientItems, breezeRecipient);
        }

        // Items might have been added/removed so must reorder the items before then saving
        this.estimatesRepo.OrderEstimateItemsTree(categoryItems);
        await this.estimatesRepo.Save(null, 'Estimate item costs updated');
        await this.jobsRepo.SyncJobItemsWithEstimate(breezeEstimate);
        this.jobsRepo.SyncJobContractTotalWithEstimate(breezeEstimate);
    }

    undoQuoteRequestRecipient(
        estimate: Estimate,
        recipientItems: BreezeCase<QuoteRequestRecipientItem>[],
        quoteRequestRecipient: BreezeCase<QuoteRequestRecipient>
    ) {
        quoteRequestRecipient.IsSelectedQuote = false;

        // Remove recipe items for the recipient
        estimate.EstimateItems.forEach((estimateItem) => {
            if (
                estimateItem.ID_QuoteRequestRecipient === quoteRequestRecipient.ID_QuoteRequestRecipient &&
                estimateItem.ID_QuoteRequestRecipientRecipeItem &&
                !estimateItem.ID_QuoteRequestRecipientItem
            ) {
                if (estimateItem.IsRecipientAdded) {
                    estimateItem.ID_QuoteRequest = null;
                    estimateItem.IsDeleted = true;
                }

                estimateItem.ID_QuoteRequestRecipient = null;
                estimateItem.ID_QuoteRequestRecipientRecipeItem = null;
            }
        });

        // Remove items inside categories that were created for the recipient
        estimate.EstimateItems.forEach((estimateItem) => {
            if (
                estimateItem.ID_QuoteRequestRecipient === quoteRequestRecipient.ID_QuoteRequestRecipient &&
                estimateItem.ID_QuoteRequestRecipientItem &&
                estimateItem.ParentID
            ) {
                if (estimateItem.IsRecipientAdded) {
                    estimateItem.ID_QuoteRequest = null;
                    estimateItem.IsDeleted = true;
                } else {
                    const quoteRecipientItem = recipientItems.find((item) => item.ID_QuoteRequestItem === estimateItem.ID_QuoteRequestItem);
                    if (quoteRecipientItem) {
                        estimateItem.UnitCost = 0;
                    }
                }

                estimateItem.ID_QuoteRequestRecipient = null;
                estimateItem.ID_QuoteRequestRecipientItem = null;
            }
        });

        // Remove categories that were created for the recipient if all their children are deleted
        estimate.EstimateItems.forEach((estimateItem) => {
            if (
                estimateItem.ID_QuoteRequestRecipient === quoteRequestRecipient.ID_QuoteRequestRecipient &&
                estimateItem.ID_QuoteRequestRecipientItem &&
                !estimateItem.ParentID
            ) {
                if (estimateItem.IsRecipientAdded) {
                    estimateItem.ID_QuoteRequest = null;

                    if (!estimateItem.children?.find((item) => !item.IsDeleted)) {
                        estimateItem.IsDeleted = true;
                    }
                }

                estimateItem.ID_QuoteRequestRecipient = null;
                estimateItem.ID_QuoteRequestRecipientItem = null;
            }
        });
    }

    doQuoteRequestRecipient(
        estimate: Estimate,
        categoryItems: EstimateItem[],
        recipientItems: BreezeCase<QuoteRequestRecipientItem>[],
        quoteRequestRecipient: BreezeCase<QuoteRequestRecipient>
    ) {
        quoteRequestRecipient.IsSelectedQuote = true;

        const estimateItemById = new Map();
        const estimateItemByRequest = new Map();
        const estimateItemByRequestRecipe = new Map();
        const estimateItemByGroup = new Map();

        estimate.EstimateItems.forEach((estimateItem) => {
            estimateItemById.set(estimateItem.ID_EstimateItem, estimateItem);
            if (estimateItem.ID_QuoteRequestItem) {
                estimateItemByRequest.set(estimateItem.ID_QuoteRequestItem, estimateItem);
            }
            if (estimateItem.ID_QuoteRequestRecipeItem) {
                estimateItemByRequestRecipe.set(estimateItem.ID_QuoteRequestRecipeItem, estimateItem);
            }
        });

        recipientItems.forEach((recipientItem) => {
            if (recipientItem.IsSelected) {
                if (recipientItem.IsAddedByRecipient) {
                    let categoryItem;

                    if (recipientItem.QuoteRequestRecipientGroup.IsAddedByRecipient) {
                        categoryItem = estimateItemByGroup.get(recipientItem.QuoteRequestRecipientGroup.ID_QuoteRequestRecipientGroup);

                        let description = recipientItem.QuoteRequestRecipientGroup.Description;
                        if (categoryItems.find((item) => item.Description === description && !item.IsDeleted)) {
                            let nextNumber = 1;
                            const match = `${description} (`;
                            categoryItems.forEach((item) => {
                                if (item.Description.startsWith(match)) {
                                    nextNumber = parseInt(item.Description.substring(match.length)) + 1;
                                }
                            });
                            description = `${description} (${nextNumber})`;
                        }

                        if (!categoryItem) {
                            categoryItem = this.estimatesRepo.CreateEstimateItem(estimate, {
                                IsParent: true,
                                Description: description,
                                FromRFQ: false,
                            });

                            categoryItem.IsRecipientAdded = true;
                            categoryItem.ID_QuoteRequest = recipientItem.ID_QuoteRequest;
                            categoryItem.ID_QuoteRequestRecipient = recipientItem.ID_QuoteRequestRecipient;
                            categoryItem.children = [];
                            categoryItems.push(categoryItem);
                            estimateItemByGroup.set(recipientItem.QuoteRequestRecipientGroup.ID_QuoteRequestRecipientGroup, categoryItem);
                        }
                    } else {
                        categoryItem = estimateItemById.get(recipientItem.QuoteRequestRecipientGroup.QuoteRequestGroup.ID_EstimateItem);
                    }

                    const childItem = this.estimatesRepo.CreateEstimateItem(estimate, {
                        ParentID: categoryItem.ID_EstimateItem,
                        FromRFQ: false,
                    });

                    childItem.IsRecipientAdded = true;
                    childItem.ID_QuoteRequest = recipientItem.ID_QuoteRequest;
                    childItem.children = [];
                    categoryItem.children.push(childItem);
                    this.PopulateEstimateItemFromRecipientItem(estimateItemByRequestRecipe, recipientItem, estimate, childItem);
                } else {
                    const estimateItem = estimateItemByRequest.get(recipientItem.ID_QuoteRequestItem);
                    this.PopulateEstimateItemFromRecipientItem(estimateItemByRequestRecipe, recipientItem, estimate, estimateItem);
                }
            }
        });
    }

    // @ts-ignore TS7006
    PopulateEstimateItemFromRecipientItem(estimateItemByRequestRecipe, recipientItem, estimate, estimateItem) {
        estimateItem.ID_QuoteRequestRecipient = recipientItem.ID_QuoteRequestRecipient;
        estimateItem.ID_QuoteRequestRecipientItem = recipientItem.ID_QuoteRequestRecipientItem;

        if (recipientItem.Quantity) {
            estimateItem.Units = recipientItem.Quantity;
        }

        if (recipientItem.Description) {
            estimateItem.Description = recipientItem.Description;
        }

        if (recipientItem.UOM) {
            estimateItem.UOM = recipientItem.UOM;
        }

        if (recipientItem.CostItemType) {
            estimateItem.CostItemType = recipientItem.CostItemType;
        }

        estimateItem.ItemCode = recipientItem.ItemCode;

        let totalWithoutTax = +recipientItem.TaxStatus === 1 ? recipientItem.Total : recipientItem.Total - recipientItem.Tax;
        estimateItem.UnitCost = totalWithoutTax / estimateItem.Units;
        estimateItem.Tax = recipientItem.Tax;

        if (recipientItem.CalculationMethod >= 1) {
            estimateItem.TaxRates = this.taxService.convertExpenseRatesToIncome(
                estimate.TaxRates,
                recipientItem.TaxRates,
                recipientItem.CostItemType
            );
        }

        if (recipientItem.IsRecipe) {
            // @ts-ignore TS7006
            recipientItem.QuoteRequestRecipientRecipeItems.sort(this.SortExistingItemsFirst).forEach((recipeItem) => {
                if (recipeItem.IsSelected) {
                    if (recipeItem.IsAddedByRecipient) {
                        const estimateRecipeItem = this.estimatesRepo.CreateEstimateItem(estimate, {
                            ParentID: estimateItem.ID_EstimateItem,
                            FromRFQ: false,
                        });

                        estimateRecipeItem.IsRecipientAdded = true;
                        estimateRecipeItem.ID_QuoteRequest = recipientItem.ID_QuoteRequest;
                        estimateItem.children.push(estimateRecipeItem);
                        this.PopulateEstimateItemFromRecipientRecipeItem(recipeItem, estimate, estimateRecipeItem);
                    } else {
                        const estimateItem = estimateItemByRequestRecipe.get(recipeItem.ID_QuoteRequestRecipeItem);
                        this.PopulateEstimateItemFromRecipientRecipeItem(recipeItem, estimate, estimateItem);
                    }
                }
            });
        }
    }

    SortExistingItemsFirst(l: BreezeCase<QuoteRequestRecipientItem>, r: BreezeCase<QuoteRequestRecipientItem>) {
        // Items added by the recipient always go after existing items
        if (l.IsAddedByRecipient && !r.IsAddedByRecipient) {
            return 1;
        } else if (!l.IsAddedByRecipient && r.IsAddedByRecipient) {
            return -1;
        }
        return l.OrderNumber - r.OrderNumber;
    }

    // @ts-ignore TS7006
    PopulateEstimateItemFromRecipientRecipeItem(recipeItem, estimate, estimateItem) {
        estimateItem.ID_QuoteRequestRecipient = recipeItem.ID_QuoteRequestRecipient;
        estimateItem.ID_QuoteRequestRecipientRecipeItem = recipeItem.ID_QuoteRequestRecipientRecipeItem;

        if (recipeItem.Description) {
            estimateItem.Description = recipeItem.Description;
        }

        if (recipeItem.UOM) {
            estimateItem.UOM = recipeItem.UOM;
        }

        if (recipeItem.CostItemType) {
            estimateItem.CostItemType = recipeItem.CostItemType;
        }

        estimateItem.UOMMinOrderQty = recipeItem.UOMMinOrderQty;
        estimateItem.UOMOrderIncrements = recipeItem.UOMOrderIncrements;
        estimateItem.Units = recipeItem.Quantity ?? 1;
        estimateItem.UOMUnits = recipeItem.UOMUnits ?? 1;
        estimateItem.ItemCode = recipeItem.ItemCode;

        const totalWithoutTax = +recipeItem.TaxStatus === 1 ? recipeItem.Total : recipeItem.Total - recipeItem.Tax;
        estimateItem.UnitCost = totalWithoutTax / (recipeItem.UOMUnits ?? 1);
        estimateItem.Tax = recipeItem.Tax;

        if (recipeItem.CalculationMethod >= 1) {
            estimateItem.TaxRates = this.taxService.convertExpenseRatesToIncome(
                estimate.TaxRates,
                recipeItem.TaxRates,
                recipeItem.CostItemType
            );
        }
    }

    // @ts-ignore TS7006
    viewQuoteRequestRecipient(id, recipientId) {
        this.quoteRequestsRepo.Get(id).then((request) => {
            // @ts-ignore TS2571
            const recipient = request.QuoteRequestRecipients.find((r) => r.ID_QuoteRequestRecipient === recipientId);
            // @ts-ignore TS2571
            if (!request.IsDeleted && recipient) {
                this.viewQuoteRequestRecipientPanel(id, recipientId).catch(() => ({}));
            } else {
                this.dialogService.show({
                    title: 'RFQ response cannot be accessed',
                    text: 'This Request for Quote response cannot be accessed. Either the RFQ or the response has been deleted.',
                    showCancel: false,
                    type: 'warning',
                });
            }
        });
    }

    unLinkPurchaseOrderFromRFQ(order: PurchaseOrder) {
        this.jobsRepo
            .GetPurchaseOrderQuoteRequest(order.ID_Reference, order.ID_PurchaseOrder)
            .then((data) => {
                if (data.results.length) {
                    const quoteReq = data.results[0];
                    // @ts-ignore TS2532
                    quoteReq.ID_PurchaseOrder = null;
                    this.quoteRequestsRepo.Save(quoteReq, 'Quote Request Updated');
                }
            })
            .catch((error) => {
                this.logger.error(error);
            });
    }

    isQuoteRequestExpired(dateDeadline: Date): boolean {
        const todayStartOfDay = moment().startOf('day');
        return moment(dateDeadline).isAfter(todayStartOfDay);
    }

    getDefaultTaxForNewItem(orderTaxRate: string, costItemType: string): string {
        return this.taxService.resetItemTaxCodes(orderTaxRate, TaxTypeEnum.Expense, costItemType);
    }

    async getQuoteRequestResponseData(quoteRequestId: Uuid, jobId: Uuid, selectedRecipientId: Uuid) {
        const [breezeQuoteRequestEntity, breezeRecipientItemEntities, breezeJobItemEntities, breezeOrderEntities] = await Promise.all([
            this.quoteRequestsRepo.Get(quoteRequestId),
            this.quoteRequestsRepo.GetQuoteRequestRecipientItems(quoteRequestId, selectedRecipientId),
            this.jobsRepo.GetJobItems(jobId),
            this.jobsRepo.GetJobPurchaseOrdersWithInvoices(jobId),
        ]);

        const breezeQuoteRequest = breezeQuoteRequestEntity as unknown as BreezeCase<QuoteRequest>;
        const breezeRecipientItems = breezeRecipientItemEntities.results as unknown as BreezeCase<QuoteRequestRecipientItem>[];
        const breezeJobItems = breezeJobItemEntities.results as unknown as BreezeCase<JobItem>[];
        const breezeOrders = breezeOrderEntities.results as unknown as BreezeCase<PurchaseOrder>[];
        return {
            quoteRequest: breezeQuoteRequest,
            recipientItems: breezeRecipientItems,
            jobItems: breezeJobItems,
            purchaseOrders: breezeOrders,
        };
    }

    unlinkVariationFromRFQ(variation: JobVariation) {
        this.jobsRepo
            .GetVariationLinkedQuoteRequest(variation.ID_Job, variation.ID_Variation)
            .then((data) => {
                if (data.results.length) {
                    const quoteReq = data.results[0];
                    // @ts-ignore TS2532
                    quoteReq.ID_Variation = null;
                    this.quoteRequestsRepo.Save(quoteReq, 'Quote Request Updated');
                }
            })
            .catch((error) => {
                this.logger.error(error);
            });
    }

    getQuoteRequestRecipientItems(quoteRequestId: string, quoteRequestRecipientId: string) {
        return this.quoteRequestsRepo
            .GetQuoteRequestRecipientItems(quoteRequestId, quoteRequestRecipientId)
            .then((res) => {
                return res;
            })
            .catch((error) => {
                this.logger.error(error);
            });
    }

    getUnitCostForRFQResponseItem(recipientItem: BreezeCase<QuoteRequestRecipientItem>): number {
        // @ts-ignore TS2532
        return Round((recipientItem.TotalIncTax - recipientItem.Tax) / recipientItem.Quantity, 4);
    }
}
